import type { ReactNode } from 'react';

import { Icon } from './Icon';
import type { IconName } from './Icon';

/**
 * Der aufklappbare Verlauf -- F1-33, K-15, K-16.
 *
 * **Warum aufklappbar.** Ein lang laufendes Ticket hat vierzig Beitraege. Als
 * Liste ausgeschriebener Texte ist das keine Uebersicht, sondern eine Wand,
 * durch die man rollt. Zugeklappt zeigt eine Zeile genau das, was die
 * Entscheidung "oeffnen oder nicht" braucht: Art, Verfasser, erste Zeile,
 * Zeitpunkt.
 *
 * **Warum ein gemeinsames Bauteil fuer Beitraege und Protokoll.** Beide
 * Reiter am Ticket beantworten dieselbe Frage -- was ist wann geschehen --
 * und sahen bisher verschieden aus, obwohl sie dasselbe Markup benutzten.
 * Wer den einen bedienen kann, soll den anderen bedienen koennen.
 *
 * **Der Zustand liegt beim Aufrufer.** Nur so lassen sich "alle ausklappen"
 * und ein Filter darueber bauen, ohne dass zwei Stellen dasselbe wissen
 * muessen. `useTimelineOpen` nimmt dem Aufrufer die Buchfuehrung ab.
 */

/** Die Art eines Eintrags -- sie bestimmt Farbe, Sinnbild und Einrueckung. */
export type TimelineKind = 'out' | 'in' | 'internal' | 'phone' | 'system' | 'neutral';

interface TimelineEntryProps {
  kind: TimelineKind;
  icon: IconName;
  /** Kennzeichen links: Art des Beitrags, Entwurf, intern. */
  badges?: ReactNode;
  /** Wer geschrieben hat. Beim Systemvermerk steht hier, was geschehen ist. */
  who: ReactNode;
  /** Die erste Zeile, solange der Eintrag zu ist. */
  preview?: string;
  time: string;
  open: boolean;
  onToggle: () => void;
  /**
   * Ob der Eintrag ueberhaupt etwas zum Aufklappen hat.
   *
   * Ein Systemvermerk ("Prioritaet auf Hoch gesetzt") sagt in seiner Zeile
   * bereits alles. Ein Pfeil daran waere ein Versprechen auf mehr, das
   * nicht eingeloest wird.
   */
  collapsible?: boolean;
  children?: ReactNode;
  /** Was unter dem Beitrag steht: Anhaenge, Schaltflaechen. */
  foot?: ReactNode;
  /** Beschriftung des Aufklappknopfs fuer vorlesende Programme. */
  toggleLabel: string;
  /**
   * Ein zusaetzlicher Rahmen -- F1-50.
   *
   * Fuer Beitraege, die seit der letzten Antwort aus dem Haus hereinkamen.
   * Er liegt **ueber** der Kante, die die Art faerbt, und ersetzt sie
   * nicht: Beide Aussagen gelten gleichzeitig.
   */
  highlighted?: boolean;
}

export function TimelineEntry({
  kind,
  icon,
  badges,
  who,
  preview,
  time,
  open,
  onToggle,
  collapsible = true,
  children,
  foot,
  toggleLabel,
  highlighted = false,
}: TimelineEntryProps) {
  const expanded = collapsible ? open : false;

  const head = (
    <>
      {collapsible ? (
        <span className="timeline__chevron">
          <Icon name="chevron" />
        </span>
      ) : (
        // Derselbe Platz, damit die Sinnbilder aller Eintraege in einer
        // Flucht stehen -- auch die der Zeilen ohne Pfeil.
        <span className="timeline__chevron" style={{ width: '0.9375rem' }} />
      )}

      <span className="timeline__mark">
        <Icon name={icon} />
      </span>

      {badges}

      <span className="timeline__who">{who}</span>

      {!expanded && preview ? <span className="timeline__preview">{preview}</span> : null}

      <span className="timeline__time">{time}</span>
    </>
  );

  return (
    <li
      className={`timeline__entry timeline__entry--${kind}${
        highlighted ? ' timeline__entry--unanswered' : ''
      }`}
    >
      {collapsible ? (
        <button
          type="button"
          className="timeline__toggle"
          aria-expanded={expanded}
          aria-label={toggleLabel}
          onClick={onToggle}
        >
          {head}
        </button>
      ) : (
        <div className="timeline__toggle" style={{ cursor: 'default' }}>
          {head}
        </div>
      )}

      {expanded ? (
        <div className="timeline__body">
          {children}
          {foot ? <div className="timeline__foot">{foot}</div> : null}
        </div>
      ) : null}
    </li>
  );
}

export function Timeline({ children }: { children: ReactNode }) {
  return <ol className="timeline">{children}</ol>;
}
